import { useCallback, useState } from "react"
import { FlowReorderableSlots } from "@/components/flow/flow-reorderable-slots"
import { useTranslation } from "@/hooks/use-translation"
import type { FlowAttachOutcome, FlowAttachRequest } from "@/lib/flow-attach-input"
import type { FlowSlotMove } from "@/lib/flow-slot-drag"
import { frameChainSlots, frameSlotInputs } from "@/lib/flow-frame-slots"
import { updateFlowNode } from "@/lib/flow-api"
import type { FlowChainSlot, FlowNodeRow } from "@/types/flow"

/** Первый и последний кадр перехода: те же слоты, что у цепочки. Роль — номер слота. */
export function FlowTransitionFrameSlots({
  node,
  nodes,
  projectId,
  frameFormat,
  frameRate,
  canEdit,
  locked,
  onDisconnectEdge,
  onNodeChange,
  onAttach,
}: {
  node: FlowNodeRow
  nodes: FlowNodeRow[]
  projectId: number
  frameFormat: string
  frameRate?: number
  canEdit: boolean
  locked: boolean
  onDisconnectEdge: (edgeId: number) => void
  onNodeChange: (node: FlowNodeRow) => void
  onAttach: (batch: FlowAttachRequest) => Promise<FlowAttachOutcome>
}) {
  const { t } = useTranslation("flow")
  const [busy, setBusy] = useState(false)
  const reorderLocked = !canEdit || locked || busy
  const slots = frameChainSlots(t("edges.firstFrame"), t("edges.lastFrame"))
  const inputs = frameSlotInputs(node.inputs ?? [])

  const changeSlot = useCallback(
    async (patch: FlowSlotMove) => {
      setBusy(true)
      try {
        onNodeChange(await updateFlowNode(projectId, node.id, patch))
      } finally {
        setBusy(false)
      }
    },
    [node.id, onNodeChange, projectId],
  )

  const attachToSlot = useCallback(
    async (slot: FlowChainSlot, batch: FlowAttachRequest) => {
      if (reorderLocked) return null
      return onAttach({ ...batch, slotIndex: slot.position })
    },
    [onAttach, reorderLocked],
  )

  return (
    <FlowReorderableSlots
      listKey={node.id}
      className="flex shrink-0 flex-col gap-3"
      slots={slots}
      inputs={inputs}
      nodes={nodes}
      projectId={projectId}
      frameFormat={frameFormat}
      frameRate={frameRate}
      canEdit={canEdit}
      locked={locked || busy}
      reorderLocked={reorderLocked}
      emptyHint={t("column.frameSlotEmpty")}
      onDisconnect={onDisconnectEdge}
      onAttach={attachToSlot}
      onCommit={changeSlot}
    />
  )
}
